// components/InvestmentOpportunity.jsx
"use client";

import React from "react";
import { Box, Grid, Typography, Paper, Stack, Button } from "@mui/material";
import Image from "next/image";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import Heading from "../heading";
import CtaButton from "../buttonWithArrow";

/** --------- CARD (no flipping; per-card vector already oriented) --------- */
function OpportunityCard({
    title,
    description,
    icon,
    vector,
    arcSide = "right",       // 'left' | 'right'
    arcAnchor = "middle",    // 'top' | 'middle' | 'bottom'
    edgeOffset = -8,
    badgeNudge = 0,
}) {
    const ARC = { xs: 92, md: 112 };
    const BADGE = { xs: 60, md: 68 };
    const ICON = { xs: 28, md: 32 };

    return (
        <Box
            sx={{
                // OUTER CARD SHADOW + BORDER
                position: "relative",
                borderRadius: 10,
                bgcolor: "#fff",
                border: "1px solid rgba(16,24,40,.06)",
                boxShadow: {
                    xs: "0 8px 20px rgba(16,24,40,.10), 0 2px 8px rgba(16,24,40,.06)",
                    md: "0 12px 32px rgba(16,24,40,.12), 0 6px 18px rgba(16,24,40,.08)",
                },
                transition: "box-shadow .2s ease, transform .2s ease",
                "&:hover": {
                    boxShadow:
                        "0 20px 48px rgba(16,24,40,.16), 0 10px 24px rgba(16,24,40,.10)",
                    transform: "translateY(-2px)",
                },

                // optional subtle inner highlight ring
                "&::after": {
                    content: '""',
                    position: "absolute",
                    inset: 0,
                    borderRadius: 12,
                    pointerEvents: "none",
                    boxShadow: "inset 0 1px 0 rgba(255,255,255,.6)",
                },

                // small padding wrapper you had
                p: "6px 6px 0 0",
            }}
        >
            <Paper
                elevation={0}
                sx={{
                    position: "relative",
                    borderRadius: 10,
                    p: { xs: 2.5, md: 3 },
                    bgcolor: "#f9f9f9",
                    minHeight: { xs: 180, md: 200 },
                    overflow: "hidden",
                    display: "flex",
                    flexDirection: "column",
                    alignItems: arcSide === "left" ? "end" : "start",
                    textAlign: arcSide === "left" ? "right" : "left",
                }}
            >
                <Typography variant="h6" sx={{ fontWeight: 800, color: "#0C0F13", mb: 1.25 }}>
                    {title}
                </Typography>

                <Stack
                    spacing={1.25}
                    sx={{
                        alignItems: arcSide === "left" ? "end" : "start",
                        textAlign: arcSide === "left" ? "right" : "left",
                        width: "75%",
                    }}
                >
                    {description.map((line, i) => (
                        <Typography key={i} variant="body2" sx={{ color: "#6B7280", lineHeight: 1.65 }}>
                            {line}
                        </Typography>
                    ))}
                </Stack>

                {/* ARC + BADGE */}
                <Box
                    aria-hidden
                    sx={{
                        position: "absolute",
                        ...(arcSide === "right" ? { right: edgeOffset } : { left: edgeOffset }),
                        ...(arcAnchor === "middle" && { top: "50%", transform: "translateY(-50%)" }),
                        ...(arcAnchor === "top" && { top: { xs: -6, md: -8 } }),
                        ...(arcAnchor === "bottom" && { bottom: { xs: -6, md: -8 } }),
                        width: ARC,
                        height: ARC,
                        zIndex: 1,
                        pointerEvents: "none",
                    }}
                >
                    <Box sx={{ position: "absolute", inset: 0 }}>
                        <Image src={vector} alt="" fill style={{ objectFit: "contain" }} sizes="(max-width: 900px) 92px, 112px" />
                    </Box>

                    {/* BADGE with soft shadow */}
                    {/* <Box
                        sx={{
                            position: "absolute",
                            top: `calc(50% + ${badgeNudge}px)`,
                            left: "50%",
                            transform: "translate(-50%, -50%)",
                            width: BADGE,
                            height: BADGE,
                            borderRadius: "50%",
                            bgcolor: "#f9f9f9",
                            display: "grid",
                            placeItems: "center",
                            boxShadow:
                                "0 8px 20px rgba(16,24,40,.16), 0 4px 10px rgba(16,24,40,.10)",
                            zIndex: 2,
                        }}
                    >
                        <Box sx={{ position: "relative", width: ICON, height: ICON }}>
                            <Image src={icon} alt="" fill style={{ objectFit: "contain" }} />
                        </Box>
                    </Box> */}
                </Box>
            </Paper>
        </Box>
    );
}


/** --------- SECTION --------- */
export default function InvestmentOpportunity({ data, onClick }) {
    const { title, subtitle, cardData } = data;
    return (
        <Box
            sx={{
                py: { xs: 6, md: 4 },
                backgroundImage: "url('/img/banner/grid.svg')"
            }}
        >
            <Heading title={title} subtitle={subtitle} />

            <Box className="container" style={{ maxWidth: 1040 }}>
                <Grid container spacing={3}>
                    {/* Row 1 */}
                    <Grid item xs={12} md={6}>
                        <OpportunityCard {...cardData[0]} />
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <OpportunityCard {...cardData[1]} />
                    </Grid>

                    {/* Row 2 */}
                    <Grid item xs={12} md={6}>
                        <OpportunityCard {...cardData[2]} />
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <OpportunityCard {...cardData[3]} />
                    </Grid>
                </Grid>

                <Box sx={{ display: "flex", justifyContent: "center", mt: { xs: 4, md: 6 } }}>
                    <CtaButton label="Invest Now" onClick={() => onClick()} />
                    {/* <CtaButton cta={{ label: "Invest Now", href: "#" }} /> */}
                </Box>
            </Box>
        </Box>
    );
}
